import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api/api';
import { toast } from 'sonner';
import {
    Plus, Search, ShieldCheck, X, History, Zap, Loader2,
    Shield, Activity, ArrowUpRight, Users,
    AlertOctagon, Target, ExternalLink, Calendar, AlertTriangle, Award
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
    Sheet,
    SheetContent,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from "./ui/sheet";
import { Skeleton } from './ui/skeleton';
import PageShell from './ui/PageShell';
import PageHeader from './ui/PageHeader';
import CustomerSuccessDashboard from './CustomerSuccessDashboard';
import { cn } from '../lib/utils';
import { formatDate, formatDateTime, normalizeApiError } from '../lib/i18n';

const LIFECYCLE_COLOR = {
    NEW_CUSTOMER: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    ONBOARDING: 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20',
    ADOPTING: 'bg-violet-500/10 text-violet-600 border-violet-500/20',
    ACTIVE: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
    RENEWAL: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    CHURNED: 'bg-muted text-muted-foreground border-border'
};

const RISK_COLOR = {
    LOW: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
    MEDIUM: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    HIGH: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
    CRITICAL: 'bg-destructive/10 text-destructive border-destructive/20'
};

const ONBOARDING_COLOR = {
    NOT_STARTED: 'bg-muted text-muted-foreground border-border',
    IN_PROGRESS: 'bg-primary/10 text-primary border-primary/20',
    COMPLETE: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
    BLOCKED: 'bg-destructive/10 text-destructive border-destructive/20'
};

const STRATEGY_CONFIG = {
    ENTERPRISE: { label: 'Enterprise (High Touch)', color: 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20' },
    MID_MARKET: { label: 'Mid-Market (Balanced)', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
    SMB: { label: 'SMB (Digital Touch)', color: 'bg-slate-500/10 text-slate-600 border-slate-500/20' },
    DEFAULT: { label: 'Global Default', color: 'bg-muted text-muted-foreground border-border' }
};

export default function CustomerSuccess() {
    const { t, i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    // Bounded Scope Model
    const filters = useMemo(() => ({
        view: searchParams.get('view') || 'accounts',
        risk: searchParams.get('risk') || 'ALL',
        onboarding: searchParams.get('onboarding') || 'ALL',
        lifecycle: searchParams.get('lifecycle') || 'ALL',
        ownerId: searchParams.get('ownerId') || 'ALL',
    }), [searchParams]);
    const filtersString = JSON.stringify(filters);

    const search = searchParams.get('search') || '';
    const activeAccountId = searchParams.get('accountId');

    const [profiles, setProfiles] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);

    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [activeProfile, setActiveProfile] = useState(null);
    const [activeTasks, setActiveTasks] = useState([]);

    // Add new profile modal
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [newCompanyId, setNewCompanyId] = useState('');

    // Detail edits
    const [editForm, setEditForm] = useState({});
    const [newNote, setNewNote] = useState('');
    const [saving, setSaving] = useState(false);

    // Moved before useEffect: updateFilter
    const updateFilter = useCallback((updates, value) => {
        const newParams = new URLSearchParams(searchParams);

        // Support both single key-value string usage: updateFilter('risk', 'HIGH')
        // and bulk object updates: updateFilter({ risk: 'HIGH', lifecycle: 'ACTIVE' })
        const actualUpdates = typeof updates === 'string' ? { [updates]: value } : updates;

        Object.entries(actualUpdates).forEach(([key, val]) => {
            if (val === 'ALL' || val === null || val === undefined || val === '') {
                newParams.delete(key);
            } else {
                newParams.set(key, val);
            }
        });

        setSearchParams(newParams, { replace: true });
    }, [searchParams, setSearchParams]);

    // Moved before useEffect: loadData
    const loadData = useCallback(async () => {
        try {
            const query = new URLSearchParams({
                ...filters,
                search
            }).toString();

            const [profRes, compRes, membersRes] = await Promise.all([
                api.get(`/api/success/profiles?${query}`),
                api.get('/api/companies'),
                api.get('/api/workspace/members')
            ]);
            setProfiles(profRes.data);
            setCompanies(compRes.data?.data || compRes.data || []);
            setMembers(membersRes.data || []);
        } catch (err) {
            console.error('Failed to load success profiles:', err);
        } finally {
            setLoading(false);
        }
    }, [filters, search]);

    // Moved before useEffect: openDetails
    const openDetails = useCallback(async (id) => {
        try {
            const [res, tasksRes] = await Promise.all([
                api.get(`/api/success/profiles/${id}`),
                api.get(`/api/success/profiles/${id}/tasks`)
            ]);
            setActiveProfile(res.data);
            setActiveTasks(tasksRes.data);
            setEditForm({
                lifecycleStage: res.data.lifecycleStage,
                riskLevel: res.data.riskLevel,
                onboardingStatus: res.data.onboardingStatus,
                renewalDate: res.data.renewalDate ? res.data.renewalDate.split('T')[0] : '',
                ownerId: res.data.ownerId || '',
                segment: res.data.segment || 'DEFAULT',
                notes: res.data.notes
            });
            setDetailModalOpen(true);
        } catch (err) {
            console.error('Failed to resolve accountId:', id, err);
            toast.error(err.response?.status === 404 ? t('customerSuccess.messages.profileNotFound') : t('customerSuccess.messages.accountDetailFailed'));
            // Clean up the invalid URL state immediately
            updateFilter('accountId', null);
        } finally {
            setLoading(false);
        }
    }, [updateFilter, t]);

    useEffect(() => {
        loadData();
    }, [filtersString, search, loadData]);

    // Handle Deep-Linking selection
    useEffect(() => {
        if (activeAccountId && !detailModalOpen) {
            openDetails(activeAccountId);
        } else if (!activeAccountId && detailModalOpen) {
            setDetailModalOpen(false);
        }
    }, [activeAccountId, detailModalOpen, openDetails]);

    // Cleanup local state when modal closes manually
    useEffect(() => {
        if (!detailModalOpen && activeAccountId) {
            updateFilter('accountId', null);
        }
    }, [detailModalOpen, activeAccountId, updateFilter]);

    const handleCreateProfile = async () => {
        if (!newCompanyId) return;
        setSaving(true);
        try {
            await api.post('/api/success/profiles', { companyId: newCompanyId });
            await loadData();
            setAddModalOpen(false);
            setNewCompanyId('');
            toast.success(t('customerSuccess.messages.profileCreated'));
        } catch (err) {
            toast.error(normalizeApiError(err, t, 'customerSuccess.messages.profileCreateFailed'));
        } finally {
            setSaving(false);
        }
    };

    const saveDetails = async () => {
        setSaving(true);
        try {
            await api.put(`/api/success/profiles/${activeProfile._id}`, editForm);
            // Re-fetch active profile to get new activity history
            const freshRes = await api.get(`/api/success/profiles/${activeProfile._id}`);
            setActiveProfile(freshRes.data);
            await loadData(); // refresh list
            toast.success("Profile saved");
        } catch (err) {
            toast.error('Failed to save profile');
        } finally {
            setSaving(false);
        }
    };

    const handleRunPlaybook = async () => {
        if (!activeProfile.ownerId) {
            toast.error('Owner Assignment Required: Playbooks cannot run without a CSM assigned.');
            return;
        }
        setSaving(true);
        try {
            const res = await api.post(`/api/success/profiles/${activeProfile._id}/playbook`, {});

            // Re-fetch data regardless of count to ensure UI sync
            const [freshRes, tasksRes] = await Promise.all([
                api.get(`/api/success/profiles/${activeProfile._id}`),
                api.get(`/api/success/profiles/${activeProfile._id}/tasks`)
            ]);
            setActiveProfile(freshRes.data);
            setActiveTasks(tasksRes.data);

            if (res.data.generatedCount > 0) {
                toast.success(`Success: Created ${res.data.generatedCount} tasks. (${res.data.skippedCount || 0} duplicates skipped)`);
            } else if (res.data.skippedCount > 0) {
                toast.info(`Analysis Complete: ${res.data.skippedCount} rules skipped (Already in queue or cooldown).`);
            } else if (res.data.error === 'NO_OWNER_ASSIGNED') {
                toast.error('Blocked: CSM Owner must be assigned to generate tasks.');
            } else {
                toast.info('Analysis Complete: No playbook intervention required for current health state.');
            }
        } catch (err) {
            toast.error('Failed to execute success playbook logic.');
        } finally {
            setSaving(false);
        }
    };

    const handleRefreshHealth = async () => {
        setSaving(true);
        try {
            await api.post(`/api/success/profiles/${activeProfile._id}/refresh-health`, {});
            // Re-fetch active profile to get new risk and reasons
            const freshRes = await api.get(`/api/success/profiles/${activeProfile._id}`);
            setActiveProfile(freshRes.data);
            // Updating the active form state if the system overrode risk
            setEditForm(prev => ({ ...prev, riskLevel: freshRes.data.riskLevel }));
            await loadData(); // refresh list
            toast.success("Health score refreshed");
        } catch (err) {
            toast.error('Failed to refresh health');
        } finally {
            setSaving(false);
        }
    };

    const handleCompleteReview = async () => {
        setSaving(true);
        try {
            const res = await api.post(`/api/success/profiles/${activeProfile._id}/complete-review`, {});
            setActiveProfile(res.data);
            await loadData();
            toast.success("Strategic Review logged. Cadence refreshed.");
        } catch (err) {
            toast.error('Failed to log review outcome.');
        } finally {
            setSaving(false);
        }
    };

    const handleAddNote = async () => {
        if (!newNote.trim()) return;
        setSaving(true);
        try {
            await api.post(`/api/success/profiles/${activeProfile._id}/notes`, { note: newNote });
            // Re-fetch to get new history
            const freshRes = await api.get(`/api/success/profiles/${activeProfile._id}`);
            setActiveProfile(freshRes.data);
            setNewNote('');
            toast.success("Note added");
        } catch (err) {
            toast.error('Failed to add note');
        } finally {
            setSaving(false);
        }
    };

    // Client-side layer for instant local fuzzy matching on top of backend scope
    const filteredProfiles = profiles.filter(p => {
        const query = search.toLowerCase().trim();
        if (!query) return true;
        return (
            p.companyId?.name?.toLowerCase().includes(query) ||
            p.companyId?.domain?.toLowerCase().includes(query) ||
            p.ownerId?.name?.toLowerCase().includes(query)
        );
    });

    // Companies without a profile yet
    const unmappedCompanies = companies.filter(c => !profiles.find(p => p.companyId?._id === c._id));

    if (loading) {
        return (
            <PageShell className="max-w-7xl mx-auto p-4 sm:p-8 space-y-8">
                <div className="space-y-8">
                    <div className="flex flex-col gap-2">
                        <Skeleton className="h-10 w-64 rounded-xl" />
                        <Skeleton className="h-4 w-96 rounded-lg opacity-50" />
                    </div>

                    <div className="flex justify-between items-center bg-card p-4 rounded-2xl border border-border">
                        <Skeleton className="h-10 w-48 rounded-lg" />
                        <Skeleton className="h-10 w-32 rounded-lg" />
                    </div>

                    <div className="grid grid-cols-1 gap-6">
                        <Skeleton className="h-[400px] w-full rounded-2xl" />
                    </div>
                </div>
            </PageShell>
        );
    }
    return (
        <PageShell className="space-y-6 max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">

            <PageHeader
                title={t('customerSuccess.title')}
                icon={ShieldCheck}
                description={t('customerSuccess.description')}
                actions={
                    <div className="flex items-center gap-3">
                        <div className="flex bg-muted/50 p-1 rounded-xl border border-border/50">
                            <button
                                onClick={() => updateFilter({ view: 'accounts' })}
                                className={cn(
                                    "px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all",
                                    filters.view === 'accounts' ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                                )}
                            >
                                {t('customerSuccess.views.accounts')}
                            </button>
                            <button
                                onClick={() => updateFilter({ view: 'dashboard' })}
                                className={cn(
                                    "px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all",
                                    filters.view === 'dashboard' ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                                )}
                            >
                                {t('customerSuccess.views.opsDashboard')}
                            </button>
                        </div>
                        <Button className="h-9 px-4 rounded-xl font-black uppercase tracking-widest text-[10px] gap-2 shadow-lg shadow-primary/10" onClick={() => setAddModalOpen(true)}>
                            <Plus className="w-3.5 h-3.5" /> {t('customerSuccess.actions.initializeAccount')}
                        </Button>
                    </div>
                }
            />

            {/* INTEGRATED SCOPE BAR (Sprint 3) */}
            <div className="px-8 mb-6">
                <div className="bg-card/50 border border-border/50 rounded-2xl p-2 flex flex-wrap items-center gap-2 shadow-sm">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground opacity-50" />
                        <input
                            type="text"
                            placeholder={t('customerSuccess.searchPlaceholder')}
                            className="w-full bg-background/50 border border-border/30 rounded-xl pl-9 pr-4 py-2 text-[11px] font-bold focus:ring-2 focus:ring-primary/10 transition-all outline-none"
                            value={search}
                            onChange={e => updateFilter('search', e.target.value)}
                        />
                    </div>

                    <div className="h-8 w-px bg-border/50 mx-1 hidden sm:block" />

                    {/* Risk Filter (Color Coded Label) */}
                    <div className="group relative">
                        <select
                            className={cn(
                                "bg-background/50 border border-border/30 rounded-xl px-3 py-2 text-[10px] font-black uppercase tracking-widest outline-none focus:ring-2 focus:ring-primary/10 cursor-pointer transition-all",
                                filters.risk !== 'ALL' ? RISK_COLOR[filters.risk] + " border-current/20" : "hover:border-border/60"
                            )}
                            value={filters.risk}
                            onChange={e => updateFilter('risk', e.target.value)}
                        >
                            <option value="ALL">{t('customerSuccess.filters.riskAll')}</option>
                            {Object.keys(RISK_COLOR).map(k => <option key={k} value={k}>RISK: {k}</option>)}
                        </select>
                    </div>

                    {/* Onboarding Filter */}
                    <div className="group relative">
                        <select
                            className={cn(
                                "bg-background/50 border border-border/30 rounded-xl px-3 py-2 text-[10px] font-black uppercase tracking-widest outline-none focus:ring-2 focus:ring-primary/10 cursor-pointer transition-all",
                                filters.onboarding !== 'ALL' ? ONBOARDING_COLOR[filters.onboarding] + " border-current/20" : "hover:border-border/60"
                            )}
                            value={filters.onboarding}
                            onChange={e => updateFilter('onboarding', e.target.value)}
                        >
                            <option value="ALL">{t('customerSuccess.filters.onboardingAll')}</option>
                            {Object.keys(ONBOARDING_COLOR).map(k => <option key={k} value={k}>ONBOARDING: {k.replace('_', ' ')}</option>)}
                        </select>
                    </div>

                    {/* Lifecycle Filter */}
                    <div className="group relative">
                        <select
                            className={cn(
                                "bg-background/50 border border-border/30 rounded-xl px-3 py-2 text-[10px] font-black uppercase tracking-widest outline-none focus:ring-2 focus:ring-primary/10 cursor-pointer transition-all",
                                filters.lifecycle !== 'ALL' ? LIFECYCLE_COLOR[filters.lifecycle] + " border-current/20" : "hover:border-border/60"
                            )}
                            value={filters.lifecycle}
                            onChange={e => updateFilter('lifecycle', e.target.value)}
                        >
                            <option value="ALL">{t('customerSuccess.filters.lifecycleAll')}</option>
                            {Object.keys(LIFECYCLE_COLOR).map(k => <option key={k} value={k}>LIFECYCLE: {k.replace('_', ' ')}</option>)}
                        </select>
                    </div>

                    {/* Owner Filter */}
                    <div className="group relative">
                        <select
                            className="bg-background/50 border border-border/30 rounded-xl px-3 py-2 text-[10px] font-black uppercase tracking-widest outline-none focus:ring-2 focus:ring-primary/10 cursor-pointer hover:border-border/60 transition-all"
                            value={filters.ownerId}
                            onChange={e => updateFilter('ownerId', e.target.value)}
                        >
                            <option value="ALL">{t('customerSuccess.filters.ownerAll')}</option>
                            {members.map(m => (
                                <option key={m.userId} value={m.userId}>OWNER: {(m.name || m.email).toUpperCase()}</option>
                            ))}
                        </select>
                    </div>

                    {Object.entries(filters).some(([k, v]) => k !== 'view' && v !== 'ALL' && v !== 'accounts') && (
                        <Button
                            variant="ghost"
                            className="h-8 px-3 rounded-xl text-[9px] font-black uppercase tracking-widest text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-all gap-1.5"
                            onClick={() => {
                                // Canonical Reset: Reset all but preserving view mode
                                setSearchParams({ view: filters.view }, { replace: true });
                            }}
                        >
                            <X className="w-3 h-3" /> Clear filters
                        </Button>
                    )}
                </div>
            </div>

            {filters.view === 'dashboard' ? (
                // Dashboard specifically avoids 'search' context to prevent confusing 0-result metrics
                // across time-series when only one account is visible in the list.
                <CustomerSuccessDashboard
                    onDrilldown={(drillFilters) => updateFilter({ ...drillFilters, view: 'accounts' })}
                    filters={Object.fromEntries(Object.entries(filters).filter(([k]) => k !== 'search'))}
                />
            ) : (
                <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
                    <div className="p-5 border-b border-border/50 bg-muted/20 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-[10px] font-black text-muted-foreground uppercase tracking-widest bg-background px-3 py-1.5 rounded-lg border border-border/50">
                            <Users className="w-3.5 h-3.5 text-primary" /> {filteredProfiles.length} Control Points Active
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-muted/30 border-b border-border/50">
                                    <th className="px-6 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest">Account Intelligence</th>
                                    <th className="px-4 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest">Lifecycle Status</th>
                                    <th className="px-4 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest">Health Logic</th>
                                    <th className="px-4 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest">Onboarding</th>
                                    <th className="px-4 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest">Primary Owner</th>
                                    <th className="px-6 py-4 w-16"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border/50 bg-card">
                                {filteredProfiles.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="p-20 text-center">
                                            <div className="flex flex-col items-center justify-center opacity-40">
                                                <div className="p-4 bg-muted rounded-full mb-4">
                                                    <Search className="w-8 h-8 text-muted-foreground" />
                                                </div>
                                                <p className="text-sm font-black uppercase tracking-widest text-muted-foreground">No accounts match your criteria</p>
                                                <p className="text-xs font-bold text-muted-foreground mt-1">Try adjusted filters or global search</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredProfiles.map(p => (
                                    <tr
                                        key={p._id}
                                        className="hover:bg-muted/30 transition-all cursor-pointer group"
                                        onClick={() => updateFilter('accountId', p._id)}
                                    >
                                        <td className="px-6 py-5">
                                            <div className="flex flex-col">
                                                <span className="font-black text-sm text-foreground group-hover:text-primary transition-colors uppercase tracking-tight">
                                                    {p.companyId?.name || t('customerSuccess.common.unknownCompany')}
                                                </span>
                                                <div className="flex items-center gap-1.5 mt-0.5">
                                                    <span className={cn(
                                                        "px-1.5 py-0.5 text-[8px] font-black uppercase tracking-[0.1em] rounded border opacity-70",
                                                        STRATEGY_CONFIG[p.segment || 'DEFAULT']?.color || STRATEGY_CONFIG.DEFAULT.color
                                                    )}>
                                                        {STRATEGY_CONFIG[p.segment || 'DEFAULT']?.label.split(' ')[0] || 'DEFAULT'}
                                                    </span>
                                                    <span className="text-[9px] font-bold text-muted-foreground/40 uppercase tracking-tighter">
                                                        Managed Account · ID: {p._id.toString().slice(-4)}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-5">
                                            <span className={cn(
                                                "px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border shadow-sm",
                                                LIFECYCLE_COLOR[p.lifecycleStage] || 'bg-muted text-muted-foreground border-border'
                                            )}>
                                                {p.lifecycleStage.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td className="px-4 py-5">
                                            <div className="flex flex-col items-start gap-1.5">
                                                <div className={cn(
                                                    "px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border shadow-sm flex items-center gap-1.5",
                                                    RISK_COLOR[p.riskLevel] || 'bg-muted text-muted-foreground border-border'
                                                )}>
                                                    <Activity className="w-3 h-3" /> {p.riskLevel} · SQ: {p.healthScore}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-5">
                                            <span className={cn(
                                                "px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border shadow-sm",
                                                ONBOARDING_COLOR[p.onboardingStatus] || 'bg-muted text-muted-foreground border-border'
                                            )}>
                                                {p.onboardingStatus.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td className="px-4 py-5">
                                            {p.renewalDate ? (
                                                <div className="flex flex-col items-center">
                                                    <span className="text-foreground font-bold">
                                                        {formatDate(p.renewalDate, { dateStyle: 'medium' }, i18n.resolvedLanguage)}
                                                    </span>
                                                    {(() => {
                                                        const days = Math.ceil((new Date(p.renewalDate) - new Date()) / (1000 * 60 * 60 * 24));
                                                        if (days < 0) return <span className="text-[9px] font-black text-destructive uppercase tracking-widest leading-none mt-1">Overdue</span>;
                                                        if (days <= 7) return <span className="text-[9px] font-black text-destructive uppercase tracking-widest leading-none mt-1">Critical</span>;
                                                        if (days <= 30) return <span className="text-[9px] font-black text-amber-600 uppercase tracking-widest leading-none mt-1">Urgent</span>;
                                                        return <span className="text-[9px] font-black text-muted-foreground/60 uppercase tracking-widest leading-none mt-1">Stable</span>;
                                                    })()}
                                                </div>
                                            ) : '—'}
                                        </td>
                                        <td className="px-6 py-5 text-right">
                                            <ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-20 group-hover:opacity-100 group-hover:text-primary transition-all" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ADD PROFILE MODAL */}
            <Sheet open={addModalOpen} onOpenChange={setAddModalOpen}>
                <SheetContent side="right" className="max-h-[100dvh] sm:max-h-[85vh] flex flex-col overflow-hidden sm:rounded-xl p-0">
                    <SheetHeader className="flex-shrink-0 px-6 pt-6"><SheetTitle>Add Success Profile</SheetTitle></SheetHeader>
                    <div className="py-4 flex-1 overflow-y-auto px-6">
                        <label className="block text-sm font-medium mb-1">Company Account</label>
                        <select
                            className="w-full border p-2 rounded-lg text-sm"
                            value={newCompanyId}
                            onChange={(e) => setNewCompanyId(e.target.value)}
                        >
                            <option value="">Select a company...</option>
                            {unmappedCompanies.map(c => (
                                <option key={c._id} value={c._id}>{c.name}</option>
                            ))}
                        </select>
                        {unmappedCompanies.length === 0 && <p className="text-xs text-orange-500 mt-2">All companies currently have success profiles.</p>}
                    </div>
                    <SheetFooter className="flex-shrink-0 px-6 pb-6 pt-2 bg-white border-t sm:border-t-0">
                        <Button variant="outline" onClick={() => setAddModalOpen(false)}>Cancel</Button>
                        <Button onClick={handleCreateProfile} disabled={saving || !newCompanyId}>Initialize Profile</Button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>

            {/* DETAILS DRAWER/MODAL */}
            <Sheet open={detailModalOpen} onOpenChange={setDetailModalOpen}>
                <SheetContent side="right" className="max-w-5xl h-[90vh] flex flex-col p-0 overflow-hidden border-border/50 shadow-2xl rounded-3xl">
                    {activeProfile && (
                        <>
                            {/* Identity Block - Premium Header */}
                            <div className="p-8 border-b bg-muted/30 flex justify-between items-start">
                                <div className="flex items-center gap-5">
                                    <div className="p-4 bg-primary/10 rounded-2xl border border-primary/20 text-primary shadow-sm">
                                        <ShieldCheck className="w-8 h-8" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-3 mb-1">
                                            <h2 className="text-3xl font-black tracking-tight text-foreground uppercase">{activeProfile.companyId?.name}</h2>
                                            <Badge variant="outline" className={cn("font-black tracking-widest text-[10px] uppercase px-2.5 py-1 rounded-lg", RISK_COLOR[activeProfile.riskLevel])}>
                                                {activeProfile.riskLevel} Risk
                                            </Badge>
                                        </div>
                                        <p className="text-sm font-bold text-muted-foreground opacity-70 uppercase tracking-widest flex items-center gap-2">
                                            <Target className="w-3.5 h-3.5 text-primary" /> Strategic Account Operations Workspace
                                        </p>
                                    </div>
                                    <div className="flex flex-col items-end gap-2 pr-4">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8 px-3 rounded-xl text-[10px] font-black uppercase tracking-widest gap-2 bg-background border-border/50 hover:bg-muted"
                                            onClick={() => window.location.href = `/crm/companies?search=${activeProfile.companyId?.name}`}
                                        >
                                            <ExternalLink className="w-3 h-3" /> View in CRM
                                        </Button>
                                    </div>
                                </div>
                                <Button variant="ghost" className="h-10 w-10 p-0 rounded-xl hover:bg-destructive/10 hover:text-destructive transition-all" onClick={() => setDetailModalOpen(false)}>
                                    <X className="h-6 w-6" />
                                </Button>
                            </div>

                            <div className="flex-1 overflow-y-auto lg:overflow-hidden grid grid-cols-1 lg:grid-cols-12 min-h-0">

                                {/* LEFT COLUMN: Configuration & Health Logic (Responsive width) */}
                                <div className="col-span-1 lg:col-span-4 border-b lg:border-b-0 lg:border-r border-border/50 p-6 lg:p-8 bg-muted/10 space-y-8 lg:space-y-10">

                                    {/* Section: Core Parameters */}
                                    <div className="space-y-6">
                                        <h3 className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] mb-4 border-b border-border/50 pb-2 flex justify-between items-center">
                                            Account Parameters
                                            <Badge variant="outline" className="text-[8px] font-black opacity-50 px-1.5 py-0 border-border/30">LOCKED</Badge>
                                        </h3>

                                        <div className="space-y-4">
                                            <div className="group">
                                                <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1.5 block opacity-60">Lifecycle Mapping</label>
                                                <select
                                                    className="w-full bg-background border border-border/50 p-2.5 rounded-xl text-xs font-bold focus:ring-2 focus:ring-primary/10 transition-all outline-none appearance-none cursor-pointer group-hover:border-primary/30"
                                                    value={editForm.lifecycleStage}
                                                    onChange={e => setEditForm({ ...editForm, lifecycleStage: e.target.value })}
                                                >
                                                    {Object.keys(LIFECYCLE_COLOR).map(k => <option key={k} value={k}>{k.replace('_', ' ')}</option>)}
                                                </select>
                                            </div>

                                            <div className="group">
                                                <div className="flex justify-between items-center mb-1.5 ">
                                                    <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest block opacity-60">Account Risk Level</label>
                                                    {editForm.riskLevel !== activeProfile.riskLevel && (
                                                        <Badge variant="destructive" className="text-[8px] font-black px-1.5 py-0 shadow-sm animate-pulse-slow">MANUAL OVERRIDE</Badge>
                                                    )}
                                                </div>
                                                <select
                                                    className={cn(
                                                        "w-full bg-background border border-border/50 p-2.5 rounded-xl text-xs font-bold focus:ring-2 focus:ring-primary/10 transition-all outline-none appearance-none cursor-pointer group-hover:border-primary/30",
                                                        editForm.riskLevel !== activeProfile.riskLevel ? "border-amber-500/30 ring-amber-500/5 ring-1 bg-amber-500/5" : ""
                                                    )}
                                                    value={editForm.riskLevel}
                                                    onChange={e => setEditForm({ ...editForm, riskLevel: e.target.value })}
                                                >
                                                    {Object.keys(RISK_COLOR).map(k => <option key={k} value={k}>{k}</option>)}
                                                </select>
                                            </div>

                                            <div className="group">
                                                <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1.5 block opacity-60">Success Owner (CSM)</label>
                                                <select
                                                    className="w-full bg-background border border-border/50 p-2.5 rounded-xl text-xs font-bold focus:ring-2 focus:ring-primary/10 transition-all outline-none appearance-none cursor-pointer group-hover:border-primary/30"
                                                    value={editForm.ownerId}
                                                    onChange={e => setEditForm({ ...editForm, ownerId: e.target.value })}
                                                >
                                                    <option value="">Unassigned</option>
                                                    {members.map(m => (
                                                        <option key={m.userId} value={m.userId}>{m.name}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="group">
                                                <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1.5 block opacity-60">Strategy Profile</label>
                                                <select
                                                    className="w-full bg-background border border-border/50 p-2.5 rounded-xl text-xs font-bold focus:ring-2 focus:ring-primary/10 transition-all outline-none appearance-none cursor-pointer group-hover:border-primary/30"
                                                    value={editForm.segment}
                                                    onChange={e => setEditForm({ ...editForm, segment: e.target.value })}
                                                >
                                                    {Object.entries(STRATEGY_CONFIG).map(([k, cfg]) => (
                                                        <option key={k} value={k}>{cfg.label}</option>
                                                    ))}
                                                </select>
                                                <p className="text-[8px] font-bold text-muted-foreground uppercase mt-1 opacity-40 px-1">Defines review cadence & health sensitivity</p>
                                            </div>

                                            <div className="group">
                                                <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1.5 block opacity-60">Projected Renewal</label>
                                                <input
                                                    type="date"
                                                    className="w-full bg-background border border-border/50 p-2.5 rounded-xl text-xs font-bold focus:ring-2 focus:ring-primary/10 transition-all outline-none group-hover:border-primary/30"
                                                    value={editForm.renewalDate}
                                                    onChange={e => setEditForm({ ...editForm, renewalDate: e.target.value })}
                                                />
                                            </div>

                                            <Button className="w-full h-11 rounded-xl font-black uppercase tracking-widest text-[10px] shadow-lg shadow-primary/10" onClick={saveDetails} disabled={saving}>
                                                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                                                Apply Account Updates
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Section: Health Core Intelligence */}
                                    <div className="space-y-4 pt-6 border-t border-border/50">
                                        {/* Explainable Health Intelligence Card */}
                                        <Card className="rounded-3xl border-border/40 shadow-sm overflow-hidden bg-card/30">
                                            <CardHeader className="bg-muted/10 border-b border-border/30 py-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2.5">
                                                        <div className="p-1.5 bg-primary/10 rounded-lg text-primary">
                                                            <Activity className="w-3.5 h-3.5" />
                                                        </div>
                                                        <CardTitle className="text-[11px] font-black uppercase tracking-widest text-foreground">Health Factor Breakdown</CardTitle>
                                                    </div>
                                                    <Badge className={cn("text-[10px] font-black px-2 shadow-none border-0", RISK_COLOR[activeProfile.riskLevel])}>
                                                        {activeProfile.riskLevel}
                                                    </Badge>
                                                </div>
                                            </CardHeader>
                                            <CardContent className="p-0">
                                                <div className="divide-y divide-border/20">
                                                    {(() => {
                                                        const negativeFactors = (activeProfile.healthFactors || []).filter(f => f.impact < 0).sort((a, b) => a.impact - b.impact);
                                                        const positiveFactors = (activeProfile.healthFactors || []).filter(f => f.impact >= 0).sort((a, b) => b.impact - a.impact);

                                                        return (
                                                            <>
                                                                {/* Primary Driver Highlight */}
                                                                {negativeFactors.length > 0 && (
                                                                    <div className="p-4 bg-destructive/5 border-b border-destructive/10">
                                                                        <div className="flex items-center gap-2 mb-2">
                                                                            <AlertTriangle className="w-3 h-3 text-destructive" />
                                                                            <span className="text-[9px] font-black text-destructive uppercase tracking-widest">Primary Risk Driver</span>
                                                                        </div>
                                                                        <div className="flex items-center justify-between">
                                                                            <div className="flex flex-col gap-0.5">
                                                                                <span className="text-xs font-black text-foreground">{negativeFactors[0].label}</span>
                                                                                <span className="text-[9px] font-black text-muted-foreground uppercase opacity-60">{negativeFactors[0].category}</span>
                                                                            </div>
                                                                            <div className="text-xs font-black px-2 py-1 bg-destructive text-destructive-foreground rounded-lg">
                                                                                {negativeFactors[0].impact}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Other Factors */}
                                                                {[...negativeFactors.slice(1), ...positiveFactors].map((factor, idx) => (
                                                                    <div key={idx} className="p-4 flex items-center justify-between hover:bg-muted/20 transition-colors">
                                                                        <div className="flex flex-col gap-0.5">
                                                                            <span className="text-[11px] font-bold text-foreground">{factor.label}</span>
                                                                            <span className="text-[9px] font-black text-muted-foreground uppercase tracking-widest opacity-60 text-[8px]">{factor.category}</span>
                                                                        </div>
                                                                        <div className={cn(
                                                                            "text-[10px] font-black px-2 py-0.5 rounded-md border",
                                                                            factor.impact < 0 ? "bg-destructive/5 text-destructive border-destructive/10" :
                                                                            factor.impact > 0 ? "bg-emerald-500/5 text-emerald-600 border-emerald-500/10" :
                                                                            "bg-muted/50 text-muted-foreground border-border/50"
                                                                        )}>
                                                                            {factor.impact > 0 ? `+${factor.impact}` : factor.impact || '0'}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </>
                                                        );
                                                    })()}

                                                    {(!activeProfile.healthFactors || activeProfile.healthFactors.length === 0) && (
                                                        <div className="p-8 text-center">
                                                            <p className="text-[10px] font-black text-muted-foreground uppercase opacity-40">No factor breakdown available.</p>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="p-4 bg-muted/20 border-t border-border/30 flex flex-col gap-3">
                                                    <div className="flex justify-between items-center opacity-60">
                                                        <span className="text-[9px] font-black text-muted-foreground uppercase tracking-[0.15em]">{t('customerSuccess.common.strategicCadenceIndex', { segment: activeProfile.segment || 'DEFAULT' })}</span>
                                                        <span className="text-[10px] font-black text-foreground uppercase">
                                                            {activeProfile.lastReviewDate ? t('customerSuccess.common.lastReviewed', { date: formatDate(activeProfile.lastReviewDate, { dateStyle: 'medium' }, i18n.resolvedLanguage) }) : t('customerSuccess.common.noStrategicReview')}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between items-center pt-1 border-t border-border/10">
                                                        <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Aggregate Intelligence Score</span>
                                                        <span className="text-xl font-black text-foreground">{activeProfile.healthScore}</span>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>

                                        {/* Renewal Urgency Display */}
                                        {activeProfile.renewalDate && (
                                            <Card className="rounded-3xl border-border/40 shadow-sm overflow-hidden bg-gradient-to-br from-background to-muted/20">
                                                <CardContent className="p-5 flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="p-2.5 bg-background rounded-2xl shadow-sm border border-border/50">
                                                            <Calendar className="w-4 h-4 text-primary" />
                                                        </div>
                                                        <div>
                                                            <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-0.5 opacity-60">Renewal Horizon</p>
                                                            <p className="text-sm font-black text-foreground uppercase">{formatDate(activeProfile.renewalDate, { month: 'long', day: 'numeric', year: 'numeric' }, i18n.resolvedLanguage)}</p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        {(() => {
                                                            const days = Math.ceil((new Date(activeProfile.renewalDate) - new Date()) / (1000 * 60 * 60 * 24));
                                                            let label = 'STABLE';
                                                            let color = 'text-muted-foreground bg-muted/50 border-border/50';

                                                            if (days < 0) { label = 'OVERDUE'; color = 'text-destructive bg-destructive/10 border-destructive/20'; }
                                                            else if (days <= 7) { label = 'CRITICAL'; color = 'text-destructive bg-destructive/10 border-destructive/20'; }
                                                            else if (days <= 30) { label = 'URGENT'; color = 'text-amber-600 bg-amber-500/10 border-amber-500/20'; }

                                                            return (
                                                                <>
                                                                    <div className={cn("text-[10px] font-black px-2 py-0.5 rounded-lg border inline-block mb-1 tracking-widest", color)}>
                                                                        {label}
                                                                    </div>
                                                                    <p className="text-[11px] font-bold text-muted-foreground">{days < 0 ? Math.abs(days) + ' days past' : days + ' days left'}</p>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )}

                                        {!activeProfile.ownerId && (
                                            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-2xl flex items-center gap-3 mb-2 animate-in fade-in slide-in-from-top-1">
                                                <AlertOctagon className="w-4 h-4 text-destructive" />
                                                <p className="text-[10px] font-black text-destructive uppercase tracking-widest leading-tight">
                                                    Automation Paused: Assign an owner to resolve prerequisite.
                                                </p>
                                            </div>
                                        )}
                                        <div className="grid grid-cols-2 gap-3 pb-6 lg:pb-0">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-10 rounded-xl text-[9px] font-black uppercase tracking-widest gap-2 bg-background border-border/50 hover:bg-muted"
                                                onClick={handleRefreshHealth}
                                                disabled={saving}
                                            >
                                                <History className="w-3.5 h-3.5" /> Force Scan
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className={cn(
                                                    "h-10 rounded-xl text-[9px] font-black uppercase tracking-widest gap-2 bg-background border-border/50 hover:bg-muted group",
                                                    !activeProfile.ownerId && "opacity-50 grayscale cursor-not-allowed"
                                                )}
                                                onClick={handleRunPlaybook}
                                                disabled={saving || !activeProfile.ownerId}
                                            >
                                                <Zap className={cn("w-3.5 h-3.5 group-hover:scale-110 transition-transform", activeProfile.ownerId ? "text-amber-500" : "text-muted-foreground")} />
                                                Run Playbook
                                            </Button>
                                        </div>

                                        {/* Strategic Action Block - New in Sprint 7 */}
                                        <div className="pt-4 border-t border-border/50">
                                            <Button
                                                className={cn(
                                                    "w-full h-12 rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-xl transition-all group overflow-hidden relative",
                                                    (() => {
                                                        const daysResource = activeProfile.lastReviewDate ? Math.ceil((new Date() - new Date(activeProfile.lastReviewDate)) / (1000 * 60 * 60 * 24)) : 999;
                                                        // Subtle signaling: only pulse the background/border glow for overdue accounts
                                                        return daysResource > 90 ? "bg-primary hover:bg-primary/90 shadow-primary/20 animate-pulse-subtle" : "bg-emerald-500 hover:bg-emerald-600";
                                                    })()
                                                )}
                                                onClick={handleCompleteReview}
                                                disabled={saving}
                                            >
                                                <div className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                                                <Award className="w-4 h-4 mr-2" />
                                                Log Strategic Review
                                            </Button>
                                            <p className="text-[9px] font-black text-muted-foreground uppercase text-center mt-3 opacity-40 tracking-widest leading-none">
                                                Tracks management cadence (90d default) • Quality not implied
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* MIDDLE COLUMN: Operational Tasks & Active Playbooks (Responsive width) */}
                                <div className="col-span-1 lg:col-span-4 border-b lg:border-b-0 lg:border-r border-border/50 overflow-y-auto p-6 lg:p-8 space-y-6 lg:space-y-8 min-h-[400px]">
                                    <div className="flex items-center justify-between border-b border-border/50 pb-2 mb-4">
                                        <h3 className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em]">Active Execution Tasks</h3>
                                        <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 rounded-lg">{activeTasks.length}</Badge>
                                    </div>

                                    <div className="space-y-4">
                                        {activeTasks.length === 0 ? (
                                            <div className="p-16 text-center flex flex-col items-center border-2 border-dashed border-border/50 rounded-3xl opacity-30 mt-8">
                                                <Target className="w-10 h-10 mb-2 opacity-50" />
                                                <p className="text-[10px] font-black uppercase tracking-widest">Execution Queue Empty</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                {activeTasks.map((p, i) => (
                                                    <div key={i} className="p-5 rounded-2xl border border-border/50 bg-muted/2 hover:bg-muted/5 transition-all group shadow-sm border-l-4 border-l-primary/30">
                                                            <div className="flex justify-between items-start mb-1.5">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-black text-sm text-foreground group-hover:text-primary group-hover:underline uppercase tracking-tight transition-colors">
                                                                        {p.title}
                                                                    </span>
                                                                    <ArrowUpRight className="w-3.5 h-3.5 text-primary opacity-0 group-hover:opacity-100 transition-all" />
                                                                </div>
                                                                <span className="text-[9px] font-black bg-background text-primary border border-primary/20 px-2 py-0.5 rounded shadow-sm tracking-widest">
                                                                    SCORE: {p.healthScore}
                                                                </span>
                                                            </div>
                                                            <p className="text-[11px] font-bold text-muted-foreground/80 leading-relaxed max-w-[95%]">
                                                                {p.healthReasons && p.healthReasons.length > 0 ? p.healthReasons[0] : 'Strategic system-level escalation flag.'}
                                                            </p>
                                                        </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* RIGHT COLUMN: Strategy Logs & Timeline History (Responsive width) */}
                                <div className="col-span-1 lg:col-span-4 flex flex-col h-full bg-muted/5 min-h-[400px]">
                                    <div className="p-6 lg:p-8 border-b border-border/50 font-black text-[10px] uppercase tracking-[0.2em] flex items-center gap-3 bg-muted/20">
                                        <History className="w-4 h-4 text-primary" /> {t('customerSuccess.timeline.title')}
                                    </div>
                                    <div className="flex-1 overflow-y-auto p-6 lg:p-8 space-y-8">
                                        {activeProfile.activityHistory.length === 0 ? (
                                            <div className="p-12 text-center text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-30">{t('customerSuccess.timeline.empty')}</div>
                                        ) : (
                                            <div className="relative border-l-2 border-border/50 pl-6 ml-2 space-y-10">
                                                {[...activeProfile.activityHistory].reverse().map((act, i) => (
                                                    <div key={i} className="relative group">
                                                        <div className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full border-2 border-background bg-primary shadow-sm group-hover:scale-125 transition-transform" />
                                                        <div className="text-xs font-black text-foreground uppercase tracking-tight mb-1">{act.action.replace('_', ' ')}</div>
                                                        <div className="text-[11px] font-bold text-muted-foreground/80 leading-relaxed">{act.details}</div>
                                                        <div className="text-[9px] font-black text-muted-foreground/40 uppercase tracking-widest mt-2 flex items-center justify-between">
                                                            <span>{formatDateTime(act.timestamp, {}, i18n.resolvedLanguage)}</span>
                                                            <span className="bg-muted px-2 py-0.5 rounded-lg border border-border/50 text-muted-foreground/60">{act.performedByUserId?.name || t('customerSuccess.common.system')}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-6 border-t border-border/50 bg-background shadow-[0_-4px_24px_rgba(0,0,0,0.04)]">
                                        <div className="relative group">
                                            <textarea
                                                className="w-full bg-muted/30 border border-border/50 rounded-2xl p-4 text-xs font-bold outline-none focus:ring-2 focus:ring-primary/10 transition-all resize-none group-hover:border-primary/20"
                                                rows="3"
                                                placeholder="Log chronicled strategy or meeting metadata..."
                                                value={newNote}
                                                onChange={e => setNewNote(e.target.value)}
                                            ></textarea>
                                            <div className="absolute right-3 bottom-3">
                                                <Button size="sm" className="h-9 px-4 rounded-xl font-black uppercase tracking-widest text-[9px] shadow-sm hover:translate-y-[-1px] transition-transform active:translate-y-[1px]" onClick={handleAddNote} disabled={saving || !newNote.trim()}>
                                                    Append Logic Log
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </SheetContent>
            </Sheet>
        </PageShell>
    );
}
